// @ts-check
import { h, uid } from "./dom.js";

/**
 * Labelled form field (DS-09) with hint and inline error, wired with
 * aria-describedby. Validation happens in the app (`novalidate`), never through
 * browser bubbles.
 *
 * @param {{ label: string, control: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | HTMLElement, hint?: string, optional?: boolean }} options
 */
export function field({ label, control, hint, optional = false }) {
  if (!control.id) control.id = uid("field");
  const id = control.id;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = `${id}-error`;
  const error = h("p", { class: "ui-field__error", id: errorId, hidden: true });
  control.setAttribute(
    "aria-describedby",
    [hintId, errorId].filter(Boolean).join(" "),
  );
  return h(
    "div",
    { class: "ui-field" },
    h(
      "label",
      { class: "ui-field__label", for: id },
      label,
      optional &&
        h("span", { class: "ui-field__optional", text: " (optional)" }),
    ),
    control,
    hint && h("p", { class: "ui-field__hint", id: hintId, text: hint }),
    error,
  );
}

/**
 * Form element that always validates in the app: browser bubbles never appear and
 * the submit handler calls `validate(form)`.
 * @param {Record<string, any>} props
 * @param {...any} children
 */
export function form(props, ...children) {
  const element = h("form", props, ...children);
  element.noValidate = true;
  return element;
}

/** Messages in the product's voice for built-in constraint failures. @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} control */
export function validationMessage(control) {
  const v = control.validity;
  const name =
    control
      .closest(".ui-field")
      ?.querySelector(".ui-field__label")
      ?.firstChild?.textContent?.trim() || "This field";
  if (v.valueMissing) return `${name} is required.`;
  if (v.tooShort && "minLength" in control)
    return `${name} needs at least ${control.minLength} characters.`;
  if (v.tooLong && "maxLength" in control)
    return `${name} can be at most ${control.maxLength} characters.`;
  if (v.typeMismatch)
    return `Enter a valid ${control.type === "email" ? "email address" : control.type}.`;
  if (v.patternMismatch)
    return control.title || `${name} has an unsupported format.`;
  if (v.rangeUnderflow && "min" in control)
    return `${name} must be at least ${control.min}.`;
  if (v.rangeOverflow && "max" in control)
    return `${name} must be at most ${control.max}.`;
  return control.validationMessage;
}

/**
 * Validate a form in-app. Shows each field's first problem inline, focuses the
 * first invalid control, and returns whether the form may submit.
 * @param {HTMLFormElement} form
 */
export function validate(form) {
  form.noValidate = true;
  let first = /** @type {HTMLElement | null} */ (null);
  for (const control of /** @type {any[]} */ ([...form.elements])) {
    if (!("validity" in control) || control.disabled) continue;
    const error = /** @type {HTMLElement | null} */ (
      control.id ? form.querySelector(`#${CSS.escape(control.id)}-error`) : null
    );
    const valid = control.checkValidity();
    control.toggleAttribute("aria-invalid", !valid);
    if (error) {
      error.textContent = valid ? "" : validationMessage(control);
      error.hidden = valid;
    }
    if (!valid && !first) first = control;
  }
  first?.focus();
  return !first;
}

/** Show a server-side error on one field. @param {HTMLElement} control @param {string} message */
export function showFieldError(control, message) {
  control.setAttribute("aria-invalid", "true");
  const error = control.id
    ? document.getElementById(`${control.id}-error`)
    : null;
  if (error) {
    error.textContent = message;
    error.hidden = false;
  }
}

/**
 * Keep a save button disabled until the form differs from its saved state
 * (DS-09). Call `reset()` after a successful save.
 * @param {HTMLFormElement} form
 * @param {HTMLButtonElement} save
 */
export function trackChanges(form, save) {
  const snapshot = () =>
    JSON.stringify(
      [...form.elements].map((control) => {
        const c = /** @type {any} */ (control);
        return c.type === "checkbox" || c.type === "radio"
          ? c.checked
          : c.multiple
            ? [...c.selectedOptions].map(
                (/** @type {HTMLOptionElement} */ o) => o.value,
              )
            : c.value;
      }),
    );
  let saved = snapshot();
  const update = () => {
    save.disabled = snapshot() === saved;
  };
  form.addEventListener("input", update);
  form.addEventListener("change", update);
  update();
  return {
    reset() {
      saved = snapshot();
      update();
    },
    get dirty() {
      return snapshot() !== saved;
    },
  };
}
